import Vue from 'vue'
import { getFirestore, collection, getDocs, query, orderBy, limit } from 'firebase/firestore'
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage'
import { uid } from 'uid'

// Returns management: expected returns (open in Shopify or reported as returned by the carrier),
// registering goods that arrived, and the local history (data_sources/<ds>/returns).
const initial_state = () => ({
  selected_data_source_id: '',
  expected: {},   // data source id -> expected returns from get_expected_returns
  awaiting_owner: {}, // data source id -> fully received returns still open in Shopify
  history: {},    // data source id -> registered returns, newest first
  loading_expected: false,
  loading_history: false,
})

const state = initial_state()

const HISTORY_ROWS = 50

const call = (vm, name, payload) => vm.$functions.httpsCallable(name)(payload).then(res => res.data)

const actions = {
  select_data_source({ commit }, id){
    commit('SET_STATE', { selected_data_source_id: id })
  },
  async fetch_expected({ state, commit, rootState }){
    if(!state.selected_data_source_id) return []
    commit('SET_STATE', { loading_expected: true })
    try {
      const { expected, awaiting_owner } = await call(this._vm, 'get_expected_returns', {
        company_id: rootState.warehouse.current_company_id,
        data_source_id: state.selected_data_source_id,
      })
      commit('SET_EXPECTED', { data_source_id: state.selected_data_source_id, expected, awaiting_owner })
      return expected
    } finally {
      commit('SET_STATE', { loading_expected: false })
    }
  },
  async fetch_history({ state, commit }){
    if(!state.selected_data_source_id) return []
    commit('SET_STATE', { loading_history: true })
    try {
      const snap = await getDocs(query(
        collection(getFirestore(), 'data_sources', state.selected_data_source_id, 'returns'),
        orderBy('registered_at', 'desc'),
        limit(HISTORY_ROWS)
      ))
      const history = snap.docs.map(d => d.data())
      commit('SET_HISTORY', { data_source_id: state.selected_data_source_id, history })
      return history
    } finally {
      commit('SET_STATE', { loading_history: false })
    }
  },
  // Looks an order up in Shopify, by id or by search term (order number, customer, email, return label tracking number)
  async find_order({ state, rootState }, { order_id, query: term }){
    const { orders } = await call(this._vm, 'find_return_order', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      order_id,
      query: term,
    })
    return orders
  },
  async upload_photos({ state }, blobs){
    const refs = await Promise.all((blobs || []).map(async blob => {
      const ref = `data_sources/${state.selected_data_source_id}/return_photos/${uid(24)}.jpeg`
      await uploadBytes(storageRef(getStorage(), ref), blob)
      return { ref, created_at: new Date().toISOString() }
    }))
    return refs
  },
  // items: [{ fulfillment_line_item_id, quantity, condition, note, photos: [Blob] }]
  async register({ state, rootState, commit, dispatch }, { order_id, items, tracking_number, source, fulfillment_doc_id, without_return = false }){
    const prepared = await Promise.all(items.map(async item => ({
      fulfillment_line_item_id: item.fulfillment_line_item_id,
      name: item.name,
      quantity: item.quantity,
      condition: item.condition,
      note: item.note || '',
      photo_refs: await dispatch('upload_photos', item.photos || []),
    })))
    const { return: doc } = await call(this._vm, without_return ? 'restock_without_return' : 'register_return', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      order_id,
      items: prepared,
      tracking_number: tracking_number || null,
      source: source || 'unsolicited',
      fulfillment_doc_id: fulfillment_doc_id || null,
    })
    commit('UNSHIFT_HISTORY', { data_source_id: state.selected_data_source_id, doc })
    commit('REMOVE_EXPECTED', { data_source_id: state.selected_data_source_id, order_id: doc.order_id, fulfillment_doc_id })
    // Restocked units moved Shopify inventory; refresh the cached products so picking sees the new levels
    const uuids = [...new Set(doc.items.filter(i => i.condition === 'good' && i.uuid).map(i => i.uuid))]
    if(uuids.length) dispatch('product/get_specific_products', { uuids }, { root: true }).catch(() => null)
    return doc
  },
  // Closes an open Shopify return without refund, then drops it from the awaiting list
  async close_return({ state, rootState, commit, dispatch }, { return_id }){
    await call(this._vm, 'close_return', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      return_id,
    })
    commit('REMOVE_AWAITING', { data_source_id: state.selected_data_source_id, return_id })
    dispatch('fetch_history').catch(() => null)
  },
  // Refunds all refundable units of an open return; Shopify closes it when everything is processed
  async refund_return({ state, rootState, commit, dispatch }, { return_id }){
    const { refund } = await call(this._vm, 'refund_return', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      return_id,
    })
    commit('REMOVE_AWAITING', { data_source_id: state.selected_data_source_id, return_id })
    dispatch('fetch_history').catch(() => null)
    return refund
  },
  // Return labels: which carriers can bring the parcel back, book, attach an external one, void
  async get_label_options({ state, rootState }, { return_id }){
    return call(this._vm, 'get_return_label_options', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      return_id,
    })
  },
  async create_label({ state, rootState, dispatch }, { return_id, carrier_account_id, service_id, notify_customer }){
    const { label } = await call(this._vm, 'create_return_label', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      return_id, carrier_account_id, service_id, notify_customer,
    })
    dispatch('fetch_expected').catch(() => null)
    return label
  },
  async attach_label({ state, rootState, dispatch }, { return_id, tracking_number, tracking_url, label_url, carrier_name, notify_customer }){
    const { label } = await call(this._vm, 'attach_return_label', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      return_id, tracking_number, tracking_url: tracking_url || null, label_url: label_url || null, carrier_name: carrier_name || null, notify_customer,
    })
    dispatch('fetch_expected').catch(() => null)
    return label
  },
  async void_label({ state, rootState }, { label_id }){
    const { label } = await call(this._vm, 'void_return_label', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      label_id,
    })
    return label
  },
  async resolve_hold({ state, rootState, commit, dispatch }, { return_id, item_id, condition, note }){
    const { return: doc } = await call(this._vm, 'resolve_return_hold', {
      company_id: rootState.warehouse.current_company_id,
      data_source_id: state.selected_data_source_id,
      return_id,
      item_id,
      condition,
      note: note || '',
    })
    commit('REPLACE_HISTORY', { data_source_id: state.selected_data_source_id, doc })
    const item = doc.items.find(i => i.id === item_id)
    if(item?.condition === 'good' && item.uuid) dispatch('product/get_specific_products', { uuids: [item.uuid] }, { root: true }).catch(() => null)
    return doc
  },
}

const mutations = {
  SET_STATE(state, payload){
    for(const key in payload) Vue.set(state, key, payload[key])
  },
  SET_EXPECTED(state, { data_source_id, expected, awaiting_owner = [] }){
    Vue.set(state.expected, data_source_id, expected)
    Vue.set(state.awaiting_owner, data_source_id, awaiting_owner)
  },
  REMOVE_AWAITING(state, { data_source_id, return_id }){
    Vue.set(state.awaiting_owner, data_source_id, (state.awaiting_owner[data_source_id] || []).filter(r => r.return_id !== return_id))
  },
  REMOVE_EXPECTED(state, { data_source_id, order_id, fulfillment_doc_id }){
    const list = state.expected[data_source_id] || []
    Vue.set(state.expected, data_source_id, list.filter(e =>
      fulfillment_doc_id ? e.fulfillment_doc_id !== fulfillment_doc_id : String(e.order_id) !== String(order_id)
    ))
  },
  SET_HISTORY(state, { data_source_id, history }){
    Vue.set(state.history, data_source_id, history)
  },
  UNSHIFT_HISTORY(state, { data_source_id, doc }){
    Vue.set(state.history, data_source_id, [doc, ...(state.history[data_source_id] || []).filter(d => d.id !== doc.id)])
  },
  REPLACE_HISTORY(state, { data_source_id, doc }){
    Vue.set(state.history, data_source_id, (state.history[data_source_id] || []).map(d => d.id === doc.id ? doc : d))
  },
}

const getters = {
  get_expected: state => state.expected[state.selected_data_source_id] || [],
  get_awaiting_owner: state => state.awaiting_owner[state.selected_data_source_id] || [],
  get_history: state => state.history[state.selected_data_source_id] || [],
  // Items held for inspection, flattened so the view can resolve them one by one
  get_holds: (state, getters) => getters.get_history
    .filter(doc => doc.status === 'on_hold')
    .reduce((tot, doc) => tot.concat(doc.items.filter(i => i.status === 'on_hold').map(item => ({ ...item, return_doc: doc }))), []),
  // Stores this company fulfills, the only ones goods can come back to
  get_return_stores: (state, getters, rootState) => (rootState.warehouse.company.data_sources || [])
    .filter(ds => ds.fulfilling_company_id === rootState.warehouse.current_company_id),
  get_allowed: (state, getters, rootState, rootGetters) => !!rootGetters['warehouse/get_plan']?.allow_returns,
  // Whether any connected carrier account can book return labels at all (lane availability is checked per return)
  get_return_labels_possible: (state, getters, rootState) => (rootState.warehouse.company.carriers || []).some(c => ['fedex', 'dhl_parcel_de', 'postnord'].includes(c.type)),
}

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
}
